/**
 * Gift Service
 * Handles interactions with the gift API
 */
import { logger } from '../utils/logger';

// Get the API endpoints from environment variables
const GIFT_API_ENDPOINT = process.env.REACT_APP_GIFT_API_ENDPOINT;
const UNIFIED_PAYMENTS_ENDPOINT = process.env.REACT_APP_PAYMENTS_ENDPOINT;

/**
 * Process a gift purchase
 * 
 * @param {Object} giftData - Gift data
 * @param {string} giftData.giverFirstName - Gift giver's first name
 * @param {string} giftData.giverLastName - Gift giver's last name
 * @param {string} giftData.giverEmail - Gift giver's email
 * @param {string} giftData.recipientFirstName - Gift recipient's first name
 * @param {string} giftData.recipientLastName - Gift recipient's last name
 * @param {string} giftData.recipientEmail - Gift recipient's email
 * @param {string} giftData.message - Personal message
 * @param {string} giftData.plan - Selected plan (basic or family)
 * @param {string} giftData.paymentId - Payment ID from Stripe
 * @returns {Promise<Object>} - Gift processing result
 */
const processGift = async (giftData) => {
  try {
    // Check if unified payments endpoint is available
    if (UNIFIED_PAYMENTS_ENDPOINT) {
      logger.debug('Using unified payments endpoint for gift');
      
      // Transform the data for the unified endpoint
      const unifiedGiftData = {
        paymentType: 'gift',
        // The Lambda function expects giverFirstName, giverLastName, giverEmail
        giverFirstName: giftData.giverFirstName,
        giverLastName: giftData.giverLastName,
        giverEmail: giftData.giverEmail,
        recipientFirstName: giftData.recipientFirstName,
        recipientLastName: giftData.recipientLastName,
        recipientEmail: giftData.recipientEmail,
        message: giftData.message || '',
        plan: giftData.plan,
        // The Lambda function expects paymentMethodId, not paymentId
        paymentMethodId: giftData.paymentId || giftData.paymentMethodId
      };
      
      logger.debug('Sending gift data to unified endpoint:', unifiedGiftData);
      
      const response = await fetch(UNIFIED_PAYMENTS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(unifiedGiftData),
      });

      const data = await response.json();

      if (!response.ok) {
        logger.error('Gift validation failed:', data);
        throw new Error(data.message || 'Gift processing failed');
      }

      return data;
    }
    // Fall back to the original endpoint if unified endpoint is not available
    else if (GIFT_API_ENDPOINT) {
      logger.debug('Using legacy gift API endpoint');
      
      const response = await fetch(GIFT_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(giftData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Gift processing failed');
      }

      return data;
    } else {
      throw new Error('Gift API endpoint is not configured');
    }
  } catch (error) {
    logger.error('Gift processing error:', error);
    throw error;
  }
};

export const giftService = {
  processGift,
};
